// The Upload progress toast (owner, 2026-09-03).
//
// "Put it inside a toast box on the right side of the screen that pops-up after
// I send files to intake, stays while those files are being processed, and then
// fades-away like thirty seconds after all of the documents inside the toast are
// done."
//
// WHY THIS IS NOT A FLUENT TOAST. `useToastController` takes a SNAPSHOT of its
// content: updating a live row means calling `updateToast` on every poll, which
// would put the render loop in the polling hook. This panel is a plain fixed
// element instead, painted with the same tokens a Fluent toast uses, so it
// re-renders from state like any other component and the polling stays where it
// belongs.
//
// WHY IT IS MOUNTED IN THE APP SHELL. The progress used to live inside the
// Upload dialog, so it died the moment the dialog closed — which is the first
// thing an uploader does after pressing Send. It reads its list from
// `lib/uploadTracker`, which is module state, so the dialog opening and closing
// underneath it changes nothing.
//
// IT SITS ABOVE THE FLUENT TOASTER, not in place of it: `App.tsx` pins the
// Toaster to `bottom-end`, so this panel is offset upward to leave that corner
// free. The two never overlap.
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Caption1,
  ProgressBar,
  Spinner,
  Subtitle2,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components'
import {
  CheckmarkCircleFilled,
  DismissRegular,
  ErrorCircleFilled,
  InfoRegular,
} from '@fluentui/react-icons'
import { isTerminalStage, useUploadProgress, type UploadProgress } from '../lib/hooks'
import { clearTrackedUploads, isRegistered, useTrackedUploads, type TrackedUpload } from '../lib/uploadTracker'
import { STAGE_FRACTION, isInFlight, isSettled, stageLabel, type DisplayStage } from '../lib/uploadStatus'

/**
 * How long the finished toast stays before it starts to fade. The owner asked
 * for "like thirty seconds" — long enough to read the counts after looking away,
 * short enough that it is gone before it becomes furniture.
 */
export const LINGER_MS = 30_000
/** The fade itself. Matches the CSS transition below. */
const FADE_MS = 600

const useStyles = makeStyles({
  panel: {
    position: 'fixed',
    right: '16px',
    // Clear of the Fluent Toaster, which App.tsx pins to `bottom-end`.
    bottom: '88px',
    // BELOW EVERY DIALOG (owner, 2026-09-28). Fluent mounts its portals at
    // z-index 1,000,000; this panel used the same number, tied with them, and
    // sat on top of the Pipeline dialog. 1,000 keeps it above the page and
    // under any dialog or toast.
    zIndex: 1_000,
    width: 'min(360px, calc(100vw - 32px))',
    maxHeight: 'min(420px, calc(100vh - 160px))',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
    // The fade-away. `opacity` and `visibility` together, so a faded panel
    // cannot still swallow a click over whatever is beneath it.
    opacity: 1,
    visibility: 'visible',
    transition: `opacity ${FADE_MS}ms ease, visibility 0s linear 0s`,
  },
  fading: {
    opacity: 0,
    visibility: 'hidden',
    transition: `opacity ${FADE_MS}ms ease, visibility 0s linear ${FADE_MS}ms`,
  },
  head: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 8px 10px 14px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headText: { flex: 1, minWidth: 0 },
  count: { display: 'block', color: tokens.colorNeutralForeground3 },
  list: { overflowY: 'auto', padding: '6px 0' },
  row: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    padding: '8px 14px',
  },
  rowMain: { flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px' },
  name: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  stage: { color: tokens.colorNeutralForeground3 },
  done: { color: tokens.colorPaletteGreenForeground1 },
  bad: { color: tokens.colorPaletteRedForeground1 },
  info: { color: tokens.colorNeutralForeground3 },
  // The icon column keeps its width whichever glyph is in it, so the file names
  // do not shuffle sideways as rows settle one at a time.
  icon: { width: '20px', display: 'flex', justifyContent: 'center', paddingTop: '2px' },
})

function StageIcon({ stage, className }: { stage: DisplayStage; className: string }) {
  if (isInFlight(stage)) return <Spinner size="tiny" />
  if (stage === 'parsed') return <CheckmarkCircleFilled className={className} />
  if (stage === 'failed' || stage === 'send_failed') {
    return <ErrorCircleFilled className={className} />
  }
  return <InfoRegular className={className} />
}

export function UploadProgressToast() {
  const s = useStyles()
  const entries = useTrackedUploads()
  const [fading, setFading] = useState(false)
  // Survives the fade: the list is cleared only after the panel is invisible, so
  // the rows do not vanish out from under the animation.
  const removalRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Only a registered upload has a server row to poll. A row still sending, or
  // one that failed to send, is reported from the tracker's own state.
  const ids = useMemo(() => entries.filter(isRegistered).map((entry) => entry.id), [entries])
  const progress = useUploadProgress(ids, ids.length > 0)

  /** One row's stage, whichever side knows it. */
  const stageOf = (entry: TrackedUpload): DisplayStage =>
    // No progress yet means the first poll has not come back — the row is
    // registered and queued, which is exactly `received`.
    entry.send ?? progress.get(entry.id)?.stage ?? 'uploaded'

  const settledCount = entries.filter((entry) =>
    isRegistered(entry) ? isTerminalStage(progress.get(entry.id)?.stage) : isSettled(stageOf(entry)),
  ).length
  const allSettled = entries.length > 0 && settledCount === entries.length
  const notSent = entries.filter((entry) => entry.send === 'send_failed').length

  // THE LINGER. Starts when the LAST row settles and restarts if a later Send
  // adds a row — an upload arriving mid-linger must not be swept away with the
  // batch before it.
  useEffect(() => {
    if (!allSettled) {
      setFading(false)
      return
    }
    const fade = setTimeout(() => setFading(true), LINGER_MS)
    return () => clearTimeout(fade)
  }, [allSettled, entries.length])

  // Clear the list only once the panel has actually finished fading.
  useEffect(() => {
    if (!fading) return
    removalRef.current = setTimeout(() => clearTrackedUploads(), FADE_MS)
    return () => {
      if (removalRef.current !== undefined) clearTimeout(removalRef.current)
    }
  }, [fading])

  if (entries.length === 0) return null

  const heading = allSettled
    ? entries.length === 1 ? 'Upload Complete' : 'Uploads Complete'
    : entries.length === 1 ? 'Processing Your Document' : 'Processing Your Documents'

  return (
    <div
      className={mergeClasses(s.panel, fading && s.fading)}
      // `status`, not `notification`: this updates continuously and an assertive live
      // region would interrupt a screen-reader user on every poll.
      role="status"
      aria-live="polite"
      aria-label="Upload progress"
    >
      <div className={s.head}>
        <span className={s.headText}>
          <Subtitle2>{heading}</Subtitle2>
          <Caption1 className={s.count}>
            {settledCount} of {entries.length} processed
          </Caption1>
          {/* The Upload pop-up closes on Submit (D-205), so this is the one
              place a failed send is reported. */}
          {notSent > 0 && (
            <Caption1 className={mergeClasses(s.count, s.bad)}>
              {`${notSent === 1 ? '1 file' : `${notSent} files`} could not be sent. Upload again, or email the invoice to the intake inbox instead.`}
            </Caption1>
          )}
        </span>
        <Button
          appearance="subtle"
          size="small"
          icon={<DismissRegular />}
          aria-label="Dismiss Upload Progress"
          onClick={() => clearTrackedUploads()}
        />
      </div>
      <div className={s.list}>
        {entries.map((entry) => {
          const p: UploadProgress | undefined = isRegistered(entry) ? progress.get(entry.id) : undefined
          const stage = stageOf(entry)
          const fraction = STAGE_FRACTION[stage]
          // A file that never registered shows the API's own reason, as its row
          // on the Upload pop-up did before the pop-up closed on Submit.
          const bad = stage === 'failed' || stage === 'send_failed'
          return (
            <div key={entry.id} className={s.row}>
              <span className={s.icon}>
                <StageIcon stage={stage} className={bad ? s.bad : s.info} />
              </span>
              <span className={s.rowMain}>
                <Caption1 className={s.name} title={entry.fileName}>{entry.fileName}</Caption1>
                <Caption1
                  className={mergeClasses(
                    s.stage,
                    stage === 'parsed' && s.done,
                    bad && s.bad,
                  )}
                >
                  {stage === 'send_failed' && entry.error ? `Not sent — ${entry.error}` : stageLabel(stage, p)}
                </Caption1>
                {fraction !== undefined && <ProgressBar value={fraction} />}
              </span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
